import { ChangeEventHandler, CSSProperties, FC, useCallback } from "react";

import IndeterminateCheckbox from "./IndeterminateCheckbox";

// react-table v7 ships no types and the project has no @types/react-table, so
// declare the slice of the instance these two renderers actually touch.
// `original` is the caller's row data, opaque here — only the predicate the
// caller supplied knows its shape.
interface IToggleRowSelectedProps {
  onChange: ChangeEventHandler<HTMLInputElement>;
  style: CSSProperties;
  checked: boolean;
  title: string;
  indeterminate: boolean;
}

interface ISelectableRow {
  id: string;
  original: unknown;
  isSelected: boolean;
  getToggleRowSelectedProps: () => IToggleRowSelectedProps;
}

// `isRowSelectable` and `canSelectAll` are Table's own props, passed through
// `useTable`'s options — react-table assigns options onto the instance and
// spreads the instance into these renderers, so both arrive current.
interface ISelectionInstance {
  isRowSelectable: (row: unknown) => boolean;
  canSelectAll: boolean;
}

interface ISelectAllHeader extends ISelectionInstance {
  rows: ISelectableRow[];
  toggleRowSelected: (rowId: string, value: boolean) => void;
}

interface ISelectRowCell extends Pick<ISelectionInstance, "isRowSelectable"> {
  row: ISelectableRow;
}

/**
 * Header for the row-selection column.
 *
 * Deliberately does not use react-table's `getToggleAllRowsSelectedProps()`:
 * its checked and indeterminate flags come from the internal `selectedRowIds`,
 * which includes rows we never render a checkbox for. After a "select all"
 * that left the header stuck showing a selection the user could not see or
 * clear. State is derived from the selectable rows only, and toggling touches
 * only those.
 *
 */
export const SelectAllHeader: FC<ISelectAllHeader> = ({
  rows,
  toggleRowSelected,
  isRowSelectable,
  canSelectAll,
}) => {
  const selectableRows = rows.filter(row => isRowSelectable(row.original));
  const selectedCount = selectableRows.filter(row => row.isSelected).length;
  const allSelected =
    selectableRows.length > 0 && selectedCount === selectableRows.length;

  const handleChange = useCallback(() => {
    rows
      .filter(row => isRowSelectable(row.original))
      .forEach(row => toggleRowSelected(row.id, !allSelected));
  }, [rows, isRowSelectable, toggleRowSelected, allSelected]);

  return (
    <div>
      <IndeterminateCheckbox
        checked={allSelected}
        indeterminate={!allSelected && selectedCount > 0}
        disabled={!canSelectAll || selectableRows.length === 0}
        onChange={handleChange}
        // Replaced `getToggleAllRowsSelectedProps()`, which supplied both of
        // these — the row checkboxes still get them from react-table.
        title="Toggle All Rows Selected"
        style={{ cursor: "pointer" }}
      />
    </div>
  );
};

/**
 * Cell for the row-selection column. Renders nothing for a row the table is
 * not allowed to act on, so the "which rows are selectable" rule stays in one
 * place instead of being half here and half in the table's cell loop.
 */
export const SelectRowCell: FC<ISelectRowCell> = ({ row, isRowSelectable }) => {
  if (!isRowSelectable(row.original)) return null;

  return (
    <div>
      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
    </div>
  );
};
